import { Component, ElementRef, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { ScrollUtils } from '~shared/utils/scroll.utils';
import { ErrorsUtil } from '~shared/utils/errors.util';
import { AuthService } from '~app/modules/authorization/services/authorization-idam.service';
import { Router, UrlSerializer } from '@angular/router';
import { CheckEmailService } from '~app/modules/authorization/services/check.email.service';
import { TierOneFormValidationService } from '~app/modules/authorization/services/tier-one-form-validation.service';
import {
  APP_USER_IDAM_REGISTER_ROUTE,
  APP_USER_LOGIN_FLOW_TYPE_ROUTE,
  APP_USER_PASSWORDLESS_SIGNUP_ROUTE,
  ASSISTED_SALES_LOGIN_REDIRECT_ROUTE,
  CHECKOUT_CART_ROUTE,
  CountryCode,
  FeatureToggleEnum,
  MAGIC_LINK_FLOW_EXPERIMENT,
} from '~shared/constants';
import {
  ALREADY_IN_USE_TEXT,
  AssistedSalesRedirectionURLs,
  EMAIL_FIELD_VALIDATION_CONFIG,
  ENABLED_EXPERIMENT,
  FF_ENABLE_AUTH_URL_FROM_HOME_PAGE,
} from '~app/modules/authorization/constants/validate-email.constant';
// prettier-ignore
import { ValidateEmailGaTrackingService } from '~app/modules/authorization/services/validate-email-ga-tracking.service';
import { HOTJAR_EVENT_LIST } from '~app/modules/authorization/constants/hotjar-event-name-list.constant';
import { ApplicationAuthorizationActionType as AuthType } from '~shared/modules/application-event/constants';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { EMAIL_PATTERN } from '~shared/validators/pattern-validators';
import { environment } from '~env/environment';
import { catchError, switchMap } from 'rxjs/operators';
import { of, ReplaySubject } from 'rxjs';
import { FF_MAGIC_LINK_AUTH, FF_PASSWORD_LESS_SIGNUP } from '../../constants/authorization.constant';
import { PasswordlessService } from '../../services/passwordless.service';
import { HotJarEventsService } from '~app/modules/authorization/services/hot.jar.events.service';
import { GeneralHttpError } from '~shared/modules/error-handler';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { isEmptyString } from '~app/modules/generic/helpers';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { GaTrackingCheckoutService } from '~shared/modules/ga-tracking/utils/ga-tracking-checkout.service';
import {
  LOGIN_USER_TYPE_LOCAL_STORAGE_KEY,
  LOGIN_USER_TYPES,
} from '~app/core/modules/identity/constants/identity.constant';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

export const MAGIC_LINK_BAG_KEY = 'magic-link-bag';

const NON_AUTH_BACK_ROUTES = [`/${APP_USER_IDAM_REGISTER_ROUTE}`, `/${APP_USER_PASSWORDLESS_SIGNUP_ROUTE}`];

@Component({
  selector: 'app-validate-email',
  templateUrl: './validate-email.component.html',
  styleUrls: ['./validate-email.component.scss'],
})
export class ValidateEmailComponent implements OnInit, OnDestroy {
  @ViewChild('form', { read: ElementRef }) formElement: ElementRef;
  public formGroup: FormGroup;
  public isSubmitting = false;
  public config = EMAIL_FIELD_VALIDATION_CONFIG;
  checkIcon = `${environment.cdnBaseUrl()}/images/icon-small-check-filled.svg`;
  // eslint-disable-next-line no-magic-numbers
  protected unsubscribe$ = new ReplaySubject<void>(1);
  private loginDest = AuthType.DestinationLogin;
  private passwordlessLoginDest = AuthType.DestinationPasswordlessLogin;
  private registerDest = AuthType.DestinationRegister;
  private isAuthUrlEnabledForHomePage$ = this.activeFeaturesConfigcatService.isFeatureEnabledReadyForCurrentCountry$(
    FF_ENABLE_AUTH_URL_FROM_HOME_PAGE
  );
  private assistedSalesRedirectionURLs$ = this.activeFeaturesConfigcatService.getFeatureValue(
    FeatureToggleEnum.FF_SNL_CO_5307_ASSISTED_SALES_REDIRECTION_URLS,
  );

  // prettier-ignore
  constructor( // NOSONAR
    private authService: AuthService,
    private router: Router,
    private checkEmailService: CheckEmailService,
    private tierOneFormValidationService: TierOneFormValidationService,
    private registerWithEmailGaTrackingService: ValidateEmailGaTrackingService,
    private serializer: UrlSerializer,
    private formBuilder: FormBuilder,
    private optimizelyService: OptimizelyService,
    private passwordlessService: PasswordlessService,
    private hotJarEventsService: HotJarEventsService,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
  ) {
    this.hotJarEventsService.triggerHotJarEvent(HOTJAR_EVENT_LIST.accountExistenceCheck);
  }

  public ngOnInit(): void {
    this.initializeForm();
  }

  public handleClickOnSubmit(e: Event): void {
    e.preventDefault();

    if (this.isSubmitting) {
      return;
    }
    if (this.formGroup.valid) {
      this.isSubmitting = true;
      this.handleEmailValidation();
    } else {
      ScrollUtils.scrollToInvalidElement(this.formElement, this.formGroup);
      this.handleValidationEvent();
    }
  }

  public handleValidationEvent(): void {
    const errors = ErrorsUtil.collectFormErrors(this.formGroup);
    const message = ErrorsUtil.concatErrorsToString(errors);
    this.validateEmailTrackingEvent(AuthType.SignupError, message);
    this.tierOneFormValidationService.logTierOneFormValidationError(message, this.formGroup.value);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private handleEmailValidation(): void {
    const email = this.formGroup.get('email').value;

    this.checkEmailService.saveEmail(email);

    this.authService.validateEmail(email).subscribe(
      (response) => {
        const { status, message, types, tenant } = response;
        let {
          queryParams: { backUrl: backUrl = '/', authUrl: authUrl = '' },
        } = this.router.parseUrl(this.router.url);

        if (NON_AUTH_BACK_ROUTES.includes(backUrl)) {
          backUrl = '/';
        }

        const isEmployee = this.setUserLoginType(types);

        if (status && !isEmployee) {
          this.handleRegisterAccount(backUrl);
        }

        let destination = this.registerDest;

        if (message.includes(ALREADY_IN_USE_TEXT) || isEmployee) {
          this.checkEmailService.changeEmailExistenceStatus(true);
          this.handleAlreadyExistsBehavior(
            backUrl,
            email,
            response.passwordless,
            authUrl,
            isEmployee,
            tenant?.toLowerCase()
          );
          destination = response.passwordless ? this.passwordlessLoginDest : this.loginDest;
        }
        this.validateEmailTrackingEvent(AuthType.EmailCheckSubmitted, destination);
        if ([this.loginDest, this.registerDest].includes(destination)) {
          GaTrackingCheckoutService.setCheckoutLastLoginFlag(
            destination === this.loginDest ? this.loginDest : 'registration'
          );
        }
      },
      (error: GeneralHttpError) => {
        this.isSubmitting = false;
        if (error.detail?.toLocaleLowerCase().includes('email address is invalid')) {
          this.formGroup.get('email').setErrors({ invalid: true });
          this.registerWithEmailGaTrackingService.gaTrackingInvalidEmailCheckEvent();
        }
      }
    );
  }

  private setUserLoginType(types): boolean {
    if (types && types.includes(LOGIN_USER_TYPES.EMPLOYEE)) {
      localStorage.setItem(LOGIN_USER_TYPE_LOCAL_STORAGE_KEY, LOGIN_USER_TYPES.EMPLOYEE);
    } else {
      localStorage.setItem(LOGIN_USER_TYPE_LOCAL_STORAGE_KEY, LOGIN_USER_TYPES.CUSTOMER);
    }
    return types && types.includes(LOGIN_USER_TYPES.EMPLOYEE);
  }

  private initializeForm(): void {
    const emailField = new FormControl('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]);

    this.formGroup = this.formBuilder.group({ email: emailField });
  }

  private handleAlreadyExistsBehavior(
    backUrl,
    email,
    isPasswordless = false,
    authUrl = '',
    isEmployee = false,
    tenant
  ) {
    const magicLinkBag = { email, backUrl, expirationRoute: CHECKOUT_CART_ROUTE };
    const isDifferentCountry = getCountryDomainCode(this.webAppBuyerRequest).toLowerCase() !== tenant;

    if (!isEmployee && isDifferentCountry) {
      window.location.href = `${environment.appBaseUrl()}/auth/different-country-notice?tenant=${tenant}`;
    }

    if (isPasswordless && !isEmployee) {
      this.passwordlessService.prepareMagicLinkSetup(magicLinkBag);
      this.passwordlessService.triggerMagicLink();
      return;
    }

    this.optimizelyService
      .getExperimentVariant<'enabled' | 'disabled'>(MAGIC_LINK_FLOW_EXPERIMENT, null, FF_MAGIC_LINK_AUTH)
      .pipe(
        switchMap((variation) => {
          if (
            variation === ENABLED_EXPERIMENT &&
            getCountryDomainCode(this.webAppBuyerRequest) === CountryCode.Germany
          ) {
            this.passwordlessService.prepareMagicLinkSetup(magicLinkBag);
            return of(true);
          }

          return of(false);
        }),
        catchError(() => of(false))
      )
      .subscribe((shouldProceedWithMultipleLoginOptions) => {
        if (shouldProceedWithMultipleLoginOptions) {
          this.router.navigate([APP_USER_LOGIN_FLOW_TYPE_ROUTE]);
        } else {
          this.isAuthUrlEnabledForHomePage$.subscribe((isEnabled) => {
            if (isEnabled && !isEmptyString(authUrl)) {
              if (isEmployee) {
                this.assistedSalesRedirectionURLs$.subscribe((redirectionURLs) => {
                  let assistedSalesRedirectURL;
                  try {
                    assistedSalesRedirectURL = JSON.parse(redirectionURLs) as AssistedSalesRedirectionURLs;
                  } catch (error) {
                    // do nothing and fallback value will be used
                  }
                  const loginRedirectURL = assistedSalesRedirectURL?.loginBackURL ?? ASSISTED_SALES_LOGIN_REDIRECT_ROUTE
                  this.passwordlessService.normalLoginFlow(email, `/${loginRedirectURL}/`);
                });
              } else {
                window.location.href = `${atob(authUrl)}&login_id=${encodeURIComponent(email)}`;
              }
            } else {
              this.passwordlessService.normalLoginFlow(email, backUrl);
            }
          });
        }
      });
  }

  private validateEmailTrackingEvent(authType: AuthType, message: string, format?: boolean): void {
    this.registerWithEmailGaTrackingService.gaTrackingValidateEmailButtonClickedEvent(authType, message, format);
  }

  private handleRegisterAccount(backUrl: string) {
    this.passwordLessRegistration(backUrl);
  }

  private normalRegistration(backUrl) {
    const urlTree = this.router.createUrlTree([APP_USER_IDAM_REGISTER_ROUTE], {
      queryParams: { validatedEmail: true, backUrl },
    });

    const tierOneSignUpUrl = this.serializer.serialize(urlTree);
    this.router.navigateByUrl(tierOneSignUpUrl);
  }

  private passwordLessRegistration(backUrl) {
    this.activeFeaturesConfigcatService
      .isFeatureEnabledBasedOnPercentage(FF_PASSWORD_LESS_SIGNUP)
      .pipe(
        switchMap((enabled) => {
          if (enabled) {
            const urlTree = this.router.createUrlTree([APP_USER_PASSWORDLESS_SIGNUP_ROUTE], {
              queryParams: { validatedEmail: true, backUrl },
            });

            const url = this.serializer.serialize(urlTree);
            return this.router.navigateByUrl(url);
          } else {
            this.normalRegistration(backUrl);
          }

          return of(false);
        }),
        catchError(() => of(false))
      )
      .subscribe(() => {});
  }
}
